import React, { useState } from 'react';
import styled from 'styled-components';
import { colors } from '../../styles/colors';
import DecorativeElement from '../common/DecorativeElement';

const AmenitiesSection = styled.section`
  padding: 8rem 5%;
  background: ${colors.white};
  position: relative;
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const Title = styled.h2`
  text-align: center;
  font-size: 3rem;
  color: ${colors.primary};
  margin-bottom: 3rem;
  font-weight: 600;
  letter-spacing: -0.5px;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const StepsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4rem;
  padding: 0 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 2rem;
  }
`;

const StepItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const StepIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  background: ${props => props.active ? colors.accent : colors.lightGrey};
  color: ${props => props.active ? colors.white : colors.darkGrey};
  transition: all 0.3s ease;
`;

const StepTitle = styled.span`
  font-size: 1.1rem;
  color: ${props => props.active ? colors.accent : colors.darkGrey};
  font-weight: ${props => props.active ? '600' : '500'};
  text-align: center;
`;

const ContentContainer = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 4rem;
  padding: 3rem;
  background: ${colors.white};
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const IllustrationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  
  i {
    font-size: 8rem;
    color: ${colors.accent};
  }
`;

const ContentDetails = styled.div`
  h3 {
    color: ${colors.accent};
    font-size: 2rem;
    margin-bottom: 1.5rem;
  }
  
  p {
    color: ${colors.darkGrey};
    font-size: 1.1rem;
    line-height: 1.6;
    margin-bottom: 2rem;
  }
`;

const AmenitiesList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
`;

const AmenityItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: 12px;
  background: ${colors.lightGrey}10;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${colors.accent}10;
  }
`;

const AmenityIcon = styled.div`
  width: 40px;
  height: 40px;
  min-width: 40px;
  background: ${colors.accent}10;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.accent};
  font-size: 1.2rem;
`;

const AmenityText = styled.div`
  h4 {
    color: ${colors.darkGrey};
    font-size: 1rem;
    margin-bottom: 0.2rem;
    font-weight: 600;
  }
  
  p {
    color: ${colors.darkGrey};
    font-size: 0.9rem;
    opacity: 0.8;
    line-height: 1.4;
    margin: 0;
  }
`;

const Amenities = () => {
  const [activeCategory, setActiveCategory] = useState("Recreation & Leisure");
  
  const categories = [
    {
      title: "Recreation & Leisure",
      icon: "fas fa-umbrella-beach",
      description: "Indulge in a variety of recreational activities designed for your entertainment and relaxation.",
      amenities: [
        { name: "Swimming Pool", icon: "fas fa-swimming-pool", desc: "Part of luxury clubhouse" },
        { name: "Lavish Banquet Hall", icon: "fas fa-glass-cheers", desc: "In clubhouse" },
        { name: "Amphitheatre", icon: "fas fa-theater-masks", desc: "Open-air entertainment" },
        { name: "Golf Simulator", icon: "fas fa-golf-ball", desc: "Virtual golf experience" },
        { name: "Indoor Games", icon: "fas fa-gamepad", desc: "Various indoor activities" },
        { name: "Munchkin's Land", icon: "fas fa-child", desc: "Children's play area" }
      ]
    },
    {
      title: "Wellness & Health",
      icon: "fas fa-heart",
      description: "Focus on your health and wellness with our state-of-the-art facilities.",
      amenities: [
        { name: "Gymnasium", icon: "fas fa-dumbbell", desc: "In clubhouse" },
        { name: "Jacuzzi", icon: "fas fa-hot-tub", desc: "In clubhouse" },
        { name: "Yoga Room", icon: "fas fa-pray", desc: "Yoga and meditation space" },
        { name: "Elder's Zone", icon: "fas fa-users", desc: "Dedicated senior citizen area" }
      ]
    },
    {
      title: "Convenience & Security",
      icon: "fas fa-shield-alt",
      description: "Experience peace of mind with our comprehensive security and convenience features.",
      amenities: [
        { name: "Elevator", icon: "fas fa-elevator", desc: "Modern lift system" },
        { name: "Generator Backup", icon: "fas fa-plug", desc: "24/7 power backup" },
        { name: "Piped Gas", icon: "fas fa-fire", desc: "Safe gas connection" },
        { name: "Reception", icon: "fas fa-concierge-bell", desc: "With waiting lounge" },
        { name: "Ample Parking", icon: "fas fa-parking", desc: "2 parking spaces for 3BHK" }
      ]
    },
    {
      title: "Outdoor Features",
      icon: "fas fa-sun",
      description: "Enjoy the outdoors with our thoughtfully designed exterior spaces.",
      amenities: [
        { name: "Balcony", icon: "fas fa-door-open", desc: "Private outdoor space" },
        { name: "Foyer", icon: "fas fa-archway", desc: "Welcoming entrance" },
        { name: "Podium Amenities", icon: "fas fa-layer-group", desc: "Elevated lifestyle" },
        { name: "Rooftop Features", icon: "fas fa-star", desc: "Panoramic experiences" }
      ]
    }
  ];

  const activeData = categories.find(cat => cat.title === activeCategory);

  return (
    <AmenitiesSection id="amenities">
      <DecorativeElement
        type="leafPattern"
        position={{ top: '50px', left: '-50px' }}
        width="200px"
        height="200px"
        fill={colors.accent}
        opacity={0.05}
        rotate={45}
      />

      <Container>
        <Title>World-Class Amenities</Title>
        
        <StepsContainer>
          {categories.map((category) => (
            <StepItem 
              key={category.title}
              onClick={() => setActiveCategory(category.title)}
            >
              <StepIcon active={category.title === activeCategory}>
                <i className={category.icon}></i>
              </StepIcon>
              <StepTitle active={category.title === activeCategory}>
                {category.title}
              </StepTitle>
            </StepItem>
          ))}
        </StepsContainer>

        <ContentContainer>
          <IllustrationContainer>
            <i className={activeData.icon}></i>
          </IllustrationContainer>
          
          <ContentDetails>
            <h3>{activeData.title}</h3>
            <p>{activeData.description}</p>
            
            <AmenitiesList>
              {activeData.amenities.map((amenity, idx) => (
                <AmenityItem key={idx}>
                  <AmenityIcon>
                    <i className={amenity.icon}></i>
                  </AmenityIcon>
                  <AmenityText>
                    <h4>{amenity.name}</h4>
                    <p>{amenity.desc}</p>
                  </AmenityText>
                </AmenityItem>
              ))}
            </AmenitiesList>
          </ContentDetails>
        </ContentContainer>
      </Container>
    </AmenitiesSection>
  );
};

export default Amenities; 